import {CloudMimeTypesEnum} from 'src/app/utils/enums/cloud-mime-types.enum';

export const SupportedExtensions = ['pdf', 'rtf', 'kes', 'sek', 'jpg', 'jpeg', 'bmp', 'png', 'docx', 'xlsx', 'pptx', 'epub', 'alineabook'];

export const getMimeTypeEnumFromExt = (name: string, tag?: string): CloudMimeTypesEnum => {
  if (tag === 'folder') {
    return CloudMimeTypesEnum.Folder;
  }

  const patt1 = /\.[0-9a-z]{1,11}$/i;
  const extensionMatch = name.match(patt1);
  if (extensionMatch) {
    const extension = extensionMatch.toString().toLowerCase();

    switch (extension) {
      case '.pdf': return CloudMimeTypesEnum.ApplicationPdf;
      case '.rtf': return CloudMimeTypesEnum.ApplicationRtf;
      case '.kes': return CloudMimeTypesEnum.ApplicationOctetStream;
      case '.sek': return CloudMimeTypesEnum.ApplicationOctetStream;
      case '.jpg': return CloudMimeTypesEnum.ImageJpeg;
      case '.jpeg': return CloudMimeTypesEnum.ImageJpeg;
      case '.bmp': return CloudMimeTypesEnum.ImageBmp;
      case '.png': return CloudMimeTypesEnum.ImagePng;
      case '.docx': return CloudMimeTypesEnum.ApplicationMSWordOpenXML;
      case '.doc': return CloudMimeTypesEnum.ApplicationMsWord;
      case '.xlsx': return CloudMimeTypesEnum.ApplicationMsExcelOpenXML;
      case '.pptx': return CloudMimeTypesEnum.ApplicationMsPptOpenXML;
      case '.epub': return CloudMimeTypesEnum.ApplicationEpub;
      case '.alineabook': return CloudMimeTypesEnum.ApplicationBook;
      default: return CloudMimeTypesEnum.ApplicationOctetStream;
    }
  }

  // because it was also in default:
  // TODO maybe add a CloudMimeTypesEnum.Unknown to use as default?
  return CloudMimeTypesEnum.ApplicationOctetStream;
};

export const getMimeTypeEnumFromString = (mimeType: string): CloudMimeTypesEnum => {
  switch (mimeType) {
    case 'application/pdf':
      return CloudMimeTypesEnum.ApplicationPdf;
    case 'application/rtf':
      return CloudMimeTypesEnum.ApplicationRtf;
    case 'application/octet-stream':
      return CloudMimeTypesEnum.ApplicationOctetStream;
    case 'image/jpeg':
      return CloudMimeTypesEnum.ImageJpeg;
    case 'image/bmp':
      return CloudMimeTypesEnum.ImageBmp;
    case 'image/png':
      return CloudMimeTypesEnum.ImagePng;
    case 'text/rtf':
      return CloudMimeTypesEnum.TextRtf;
    case 'application/msword':
      return CloudMimeTypesEnum.ApplicationMsWord;
    case 'text/html':
      return CloudMimeTypesEnum.TextHtml;
    case '':
      return CloudMimeTypesEnum.KesNoType;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return CloudMimeTypesEnum.ApplicationMSWordOpenXML;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return CloudMimeTypesEnum.ApplicationMsExcelOpenXML;
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return CloudMimeTypesEnum.ApplicationMsPptOpenXML;
    case 'application/epub+zip':
      return CloudMimeTypesEnum.ApplicationEpub;
    case 'application/alinea+zip':
      return CloudMimeTypesEnum.ApplicationBook;
    default:
      return CloudMimeTypesEnum.ApplicationOctetStream;
  }
};

export const getExtensionFromMimeTypeString = (mimeType: string): string => {
  switch (mimeType) {
    case 'application/pdf':
      return 'pdf';
    case 'application/msword':
      return 'doc';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'docx';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'xlsx';
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'pptx';
    case 'text/rtf':
      return 'rtf';
    case 'application/epub+zip':
      return 'epub';
    case 'application/alinea+zip':
      return 'alineabook';
    default:
      return 'pdf';
  }
};

const supportedFiles: Array<string> = [
  CloudMimeTypesEnum.SharedDrive,
  CloudMimeTypesEnum.Folder,
  CloudMimeTypesEnum.ApplicationPdf,
  CloudMimeTypesEnum.TextRtf,
  CloudMimeTypesEnum.ApplicationRtf,
  CloudMimeTypesEnum.ApplicationMSWordOpenXML,
  CloudMimeTypesEnum.ApplicationMsExcelOpenXML,
  CloudMimeTypesEnum.ApplicationMsPptOpenXML,
  CloudMimeTypesEnum.ApplicationEpub,
  CloudMimeTypesEnum.ApplicationBook,
];

const supportedFilesForOcr: Array<string> = [
  CloudMimeTypesEnum.SharedDrive,
  CloudMimeTypesEnum.Folder,
  CloudMimeTypesEnum.ApplicationPdf,
  CloudMimeTypesEnum.ImageJpeg,
  CloudMimeTypesEnum.ImagePng
];

const supportedFilesForExaMode: Array<string> = [
  CloudMimeTypesEnum.SharedDrive,
  CloudMimeTypesEnum.Folder,
  CloudMimeTypesEnum.ApplicationPdf
];

const mimeTypeConvertMap: Record<string, string> = {
  'application/epub': 'application/epub+zip',
};

export function validateMimetypes(metadata: { fileName: string, mimeType?: string },
                                  ocrOnly: boolean = false, 
                                  extFmtCheck: boolean = false,
                                  isExamode: boolean = false
): boolean {
  const mimeTypesSet = ocrOnly ? supportedFilesForOcr : (isExamode ? supportedFilesForExaMode : supportedFiles);
  const isRtfAsMSWord = !!metadata.mimeType
    && metadata.mimeType === CloudMimeTypesEnum.ApplicationMsWord
    && metadata.fileName.endsWith('.rtf');
  const isMimeTypeSupported = metadata.mimeType ? mimeTypesSet.includes(metadata.mimeType) || isRtfAsMSWord : false;

  const isKes = metadata.fileName.toLowerCase().endsWith('.kes');
  const isSek = metadata.fileName.toLowerCase().endsWith('.sek');

  return  isMimeTypeSupported ||
    (!ocrOnly && extFmtCheck && (isKes || isSek) );
}

export function normalizeMimeType(rawType: string): string {
  return mimeTypeConvertMap[rawType] || rawType;
}